/**
 * The dashboard-target picker behind each view's pin control. One board pins directly, same as
 * before several boards existed; several boards open a small accessible menu — one checkable row
 * per board (checked where the view already lives, via `view.pinned_dashboard_ids`), plus
 * "+ New dashboard…" to create one and pin into it in the same flow.
 */

import { Loader2, Pin } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { DashboardSummary, ViewRecord } from "@/lib/api";

interface PinMenuProps {
  view: ViewRecord;
  dashboards: DashboardSummary[];
  pending: boolean;
  onPin: (dashboardId: string) => void;
  onUnpin: (dashboardId: string) => void;
  onCreateAndPin: () => void;
}

export function PinMenu({
  view,
  dashboards,
  pending,
  onPin,
  onUnpin,
  onCreateAndPin,
}: PinMenuProps) {
  const pinnedIds = new Set(view.pinned_dashboard_ids ?? []);
  const pinned = pinnedIds.size > 0;
  const iconClass = pinned ? "h-4 w-4 fill-current" : "h-4 w-4";
  const buttonClass = pinned
    ? "text-primary shrink-0"
    : "text-muted-foreground hover:text-primary shrink-0";

  if (dashboards.length <= 1) {
    const boardId = dashboards[0]?.id;
    return (
      <button
        type="button"
        aria-label={pinned ? "Unpin view" : "Pin view"}
        title={pinned ? "Unpin view" : "Pin to dashboard"}
        className={buttonClass}
        disabled={pending || !boardId}
        onClick={() => boardId && (pinned ? onUnpin(boardId) : onPin(boardId))}
      >
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pin className={iconClass} />}
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={pinned ? "Pinned — manage boards" : "Pin to a dashboard"}
          title={pinned ? "Pinned — manage boards" : "Pin to a dashboard"}
          disabled={pending}
          className={buttonClass}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pin className={iconClass} />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {dashboards.map((dashboard) => {
          const checked = pinnedIds.has(dashboard.id);
          return (
            <DropdownMenuCheckboxItem
              key={dashboard.id}
              checked={checked}
              onCheckedChange={() => (checked ? onUnpin(dashboard.id) : onPin(dashboard.id))}
            >
              {dashboard.name}
            </DropdownMenuCheckboxItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onCreateAndPin}>+ New dashboard…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
